import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

/**
 * PurchaseHistoryTable
 * Lists past credit purchases with downloadable invoices.
 *
 * Props:
 *  - transactions: Array<{
 *      id: string,
 *      dateTime: string,        // pre-formatted display string
 *      transactionId: string,
 *      creditsAdded: number,
 *      amount: number,
 *      paymentMethod: string,
 *      invoiceUrl?: string,
 *    }>
 *  - onDownloadInvoice: (transaction) => void
 *  - periodLabel: string  // e.g. "Showing last 12 months"
 */

const columns = [
  { key: "dateTime", label: "Date & Time" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "creditsAdded", label: "Credits Added" },
  { key: "amount", label: "Amount" },
  { key: "paymentMethod", label: "Payment Method" },
  { key: "invoice", label: "Invoice" },
];

const PurchaseHistoryTable = ({
  transactions = [],
  onDownloadInvoice,
  periodLabel = "Showing last 12 months",
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #e2e0ed",
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    backgroundColor: "#f8f7fc",
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    color: "#66647a",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #e2e0ed",
                    padding: "12px 20px",
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {transactions.map((txn) => (
              <TableRow key={txn.id}>
                <TableCell
                  sx={{
                    fontSize: "14px",
                    color: "#22223b",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  {txn.dateTime}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "14px",
                    color: "#77758a",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  #{txn.transactionId}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#22223b",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  {txn.creditsAdded} Credits
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "14px",
                    color: "#22223b",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  ₹{txn.amount}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "14px",
                    color: "#4c4a5c",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  {txn.paymentMethod}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: "14px",
                    borderBottom: "1px solid #efeef5",
                    padding: "16px 20px",
                  }}
                >
                  <Typography
                    component="span"
                    onClick={() => onDownloadInvoice && onDownloadInvoice(txn)}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#6c45ff",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    ↓ Download PDF
                  </Typography>
                </TableCell>
              </TableRow>
            ))}

            {transactions.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{
                    textAlign: "center",
                    padding: "32px 20px",
                    fontSize: "14px",
                    color: "#999",
                    borderBottom: "none",
                  }}
                >
                  No purchases yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {transactions.length > 0 && (
        <Box
          sx={{
            textAlign: "center",
            padding: "14px",
            borderTop: "1px solid #efeef5",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#999" }}>
            {periodLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PurchaseHistoryTable;